<template>
  <div class="sot">
    <!-- ═══ Ticket ohne Bestellung ═══ -->
    <template v-if="ticket.noOrder">
      <div class="sot__card">
        <div class="sot__title"><v-icon small color="rgba(0,0,0,.4)" class="mr-1">mdi-link-off</v-icon><b>Keine Bestellung zugeordnet</b></div>
        <div class="sot__meta"><span>{{ customerLabel }}</span></div>
        <div class="sot__hint">Quelp konnte keine Bestellung erkennen. Bestellungen dieser E-Mail-Adresse in TaskDash:</div>
        <div v-if="loadingCustomerOrders" class="sot__muted"><v-progress-circular indeterminate size="16" width="2" color="#7b5cd6" class="mr-2" />Bestellungen suchen…</div>
        <div v-else-if="!customerOrders.length" class="sot__muted">Keine Bestellungen zu dieser E-Mail-Adresse gefunden. Zuordnung im Quelp-Ticket vornehmen.</div>
        <div v-else class="sot__orderLinks">
          <div v-for="o in customerOrders" :key="o.uid" class="sot__orderLink" @click="openOrderModal(String(o.uid))">
            <b>{{ o.orderName }}</b>
            <span class="ov-status ov-status--sm" :class="'ov-status--' + String(o.status || '').toLowerCase()">{{ o.status }}</span>
            <span v-if="o.isExpress" class="sot__chip sot__chip--express">EXPRESS</span>
            <span class="sot__muted">{{ ago(o.orderDate) }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- ═══ Bestellung (komprimierte OrderView) ═══ -->
    <template v-else>
      <!-- Kopf -->
      <div class="sot__card">
        <div class="sot__title">
          <span class="sot__flag" v-if="flag">{{ flag }}</span>
          <a class="sot__orderName" @click="openOrder">{{ (order && order.orderName) || ticket.orderName || ticket.orderUid }}</a>
          <span v-if="order && order.status" class="ov-status" :class="'ov-status--' + order.status.toLowerCase()">{{ order.status }}</span>
          <span v-if="order && order.isExpress" class="sot__chip sot__chip--express"><v-icon x-small color="#b26a00">mdi-flash</v-icon>EXPRESS</span>
          <span v-if="order && order.fulfillHold" class="sot__chip sot__chip--hold" :title="holdTooltip"><v-icon x-small color="#b26a00">mdi-pause-circle</v-icon>HOLD (manuell)</span>
          <span v-if="order && order.resolveOnNewAddress" class="sot__chip sot__chip--hold" title="In der Versandliste gesetzt: Auto-Fulfill wartet, bis der Kunde eine neue Adresse bestätigt oder die Adresse geändert wird.">
            <v-icon x-small color="#b26a00">mdi-map-marker-alert-outline</v-icon>WARTET AUF ADRESSE
            <button type="button" class="sot__chipBtn" :disabled="releasingHold" title="Adresse ist geklärt – Hold aufheben" @click="releaseAddressHold">lösen</button>
          </span>
          <span v-else-if="order && order.supportPending" class="sot__chip sot__chip--hold" :title="supportHoldReason">
            <v-icon x-small color="#b26a00">mdi-pause-circle</v-icon>SUPPORT-HOLD
            <button v-if="quelpHeldTickets.length" type="button" class="sot__chipBtn" :disabled="releasingHold" title="Hold in Quelp lösen (Ticket bleibt offen)" @click="releaseQuelpHold">lösen</button>
          </span>
        </div>
        <div v-if="order" class="sot__meta">
          <span>{{ ago(order.orderDate) }} · {{ dateOf(order.orderDate) }} · {{ order.shopId }}_{{ order.countryCode }}</span>
        </div>
        <div v-if="order" class="sot__actions">
          <button type="button" class="sot__btn" title="In Shopify öffnen" @click="openShopify"><v-icon x-small>mdi-open-in-new</v-icon>Shopify</button>
          <button type="button" class="sot__btn" title="Loox-Reviews des Kunden" @click="openLoox"><v-icon x-small>mdi-star-outline</v-icon>Loox</button>
          <button type="button" class="sot__btn" title="Bestellstatus-Link für den Kunden kopieren" @click="copyStatusUrl"><v-icon x-small>mdi-link-variant</v-icon>Status-Link</button>
          <button type="button" class="sot__btn" title="Zur Team-Checkliste" @click="showChecklistDialog = true"><v-icon x-small>mdi-checkbox-marked-outline</v-icon>Checkliste</button>
          <button type="button" class="sot__btn" title="Mail aus Vorlage senden" @click="showMailDialog = true"><v-icon x-small>mdi-email-outline</v-icon>Mail-Vorlage</button>
          <button v-if="showFulfillOptions" type="button" class="sot__btn" :class="{ 'sot__btn--hold': order.fulfillHold }" :disabled="holdSaving" :title="order.fulfillHold ? 'Fulfillment-Hold aufheben' : 'Fulfillment anhalten'" @click="toggleFulfillHold"><v-icon x-small>{{ order.fulfillHold ? 'mdi-play-circle-outline' : 'mdi-pause-circle-outline' }}</v-icon>{{ order.fulfillHold ? 'Hold aufheben' : 'Hold' }}</button>
          <button v-if="showFulfillOptions" type="button" class="sot__btn sot__btn--dark" @click="showManualFulfill = true"><v-icon x-small color="#fff">mdi-package-variant-closed</v-icon>Manual Fulfill</button>
          <button v-if="showFulfillOptions" type="button" class="sot__btn" @click="showReproduce = true"><v-icon x-small>mdi-restart</v-icon>Reproduce</button>
          <button type="button" class="sot__btn sot__btn--danger" @click="showCancelDialog = true"><v-icon x-small color="#c62828">mdi-cancel</v-icon>Cancel</button>
        </div>
      </div>

      <div v-if="loading" class="sot__muted"><v-progress-circular indeterminate size="16" width="2" color="#7b5cd6" class="mr-2" />Bestellung laden…</div>
      <template v-else-if="order">
        <!-- Fulfillment -->
        <div v-for="fulfillerId in Object.keys(order.fulfillment || {})" :key="'f' + fulfillerId" class="sot__card">
          <div class="sot__cardHead"><v-icon small class="sot__cardIcon">mdi-truck-outline</v-icon>{{ fulfillerId }} Fulfillment</div>
          <div v-for="f in order.fulfillment[fulfillerId]" :key="f.fulfiller_id" class="sot__ff">
            <div class="sot__ffMeta">
              <span class="sot__chip" :class="f.pending ? 'sot__chip--wait' : f.success ? 'sot__chip--ok' : 'sot__chip--bad'">{{ f.pending ? 'pending' : f.success ? 'ok' : 'FAILED' }}</span>
              <span class="sot__mono">{{ f.fulfiller_id }}</span>
              <span class="sot__muted">{{ ago(f.creationDate) }} · {{ dateOf(f.creationDate) }}</span>
            </div>
            <div v-for="(item, i) in f.fulfillItems" :key="i" class="sot__ffItem">
              <div class="sot__ffItemTop"><b>{{ item.variant }}</b><span v-if="item.express" class="sot__chip sot__chip--express">EXPRESS</span><span class="sot__muted">×{{ item.quantity }}</span><a v-if="item.file" :href="item.file" target="_blank" class="sot__link">Datei</a></div>
              <div class="sot__ffItemRow">
                <span :class="item.produced ? 'sot__ok' : 'sot__no'"><v-icon x-small :color="item.produced ? '#2e7d32' : 'rgba(0,0,0,.4)'">{{ item.produced ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon> produziert</span>
                <span :class="item.shipment ? 'sot__ok' : 'sot__no'"><v-icon x-small :color="item.shipment ? '#2e7d32' : 'rgba(0,0,0,.4)'">{{ item.shipment ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon> versandt<span v-if="item.shipment && item.shipment.shippingDate" class="sot__muted"> ({{ ago(item.shipment.shippingDate) }})</span></span>
                <span v-if="item.shipment"><a :href="item.shipment.trackingUrl" target="_blank" class="sot__link">{{ item.shipment.trackingCode }}</a> · {{ item.shipment.carrier }}</span>
                <span v-if="shipmentForItem(order, item)" class="sot__ship" :style="{ color: shipmentStatusMeta(shipmentForItem(order, item).status).color }" :title="shipmentForItem(order, item).lastEventText || ''">
                  <v-icon x-small :color="shipmentStatusMeta(shipmentForItem(order, item).status).color">{{ shipmentStatusMeta(shipmentForItem(order, item).status).icon }}</v-icon>
                  {{ shipmentStatusMeta(shipmentForItem(order, item).status).label }}<span v-if="shipmentForItem(order, item).lastEventAt" class="sot__muted"> ({{ ago(shipmentForItem(order, item).lastEventAt) }}{{ shipmentForItem(order, item).lastEventLocation ? ", " + shipmentForItem(order, item).lastEventLocation : "" }})</span>
                  <span v-if="shipmentForItem(order, item).flag" class="sot__chip sot__chip--bad">{{ shipmentFlagMeta(shipmentForItem(order, item).flag).label }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tasks -->
        <div class="sot__card">
          <div class="sot__cardHead"><v-icon small class="sot__cardIcon">mdi-image-multiple-outline</v-icon>{{ tasks.length }} Tasks</div>
          <div v-if="!tasks.length" class="sot__muted">Keine Tasks zu dieser Bestellung.</div>
          <div v-for="t in tasks" :key="t.uid" class="sot__task" :class="'sot__task--' + String(t.status || '').toLowerCase()">
            <div class="sot__thumb" :style="thumbStyle(t)" @click="openTaskImage(t)"></div>
            <div class="sot__taskBody">
              <div class="sot__taskTop">
                <span class="sot__status" :class="'sot__status--' + String(t.status || '').toLowerCase()">{{ t.status }}</span>
                <span v-if="t.express" class="sot__chip sot__chip--express">EXPRESS</span>
                <span v-if="t.feedback && t.feedback.satisfied === false" class="sot__chip sot__chip--warn"><v-icon x-small color="#c62828">mdi-emoticon-sad-outline</v-icon>Feedback</span>
                <span v-if="runningEdit(t)" class="sot__chip sot__chip--run"><v-icon x-small color="#1565c0">mdi-autorenew</v-icon>KI-Edit läuft</span>
              </div>
              <div class="sot__product">{{ (t.productType && t.productType.name) || 'Produkt unbekannt' }}<span v-if="t.quantity > 1" class="sot__qty">×{{ t.quantity }}</span></div>
              <div class="sot__variant">{{ t.customPrintVariantName || t.printVariantName || '' }}</div>
              <div class="sot__extras" v-if="t.extras && t.extras.length"><span v-for="(e, i) in t.extras" :key="i">+ {{ e.name }}</span></div>
              <div class="sot__taskMeta">
                <span v-if="t.assignedToName || t.drawerName">{{ t.assignedToName || t.drawerName }}</span>
                <span v-if="t.createdAt">angelegt {{ ago(toDate(t.createdAt)) }}</span>
                <span v-if="t.feedback && t.feedback.text" :title="t.feedback.text">„{{ String(t.feedback.text).slice(0, 70) }}{{ t.feedback.text.length > 70 ? '…' : '' }}"</span>
              </div>
              <div class="sot__taskBtns">
                <button v-if="t.customizationData" type="button" class="sot__btn sot__btn--accent" @click="openLiveEditor(t)"><v-icon x-small color="#6a4fc4">mdi-palette</v-icon>Live Editor</button>
                <a v-if="thumbUrl(t)" :href="fullUrl(t)" target="_blank" class="sot__btn"><v-icon x-small>mdi-open-in-new</v-icon>Bild</a>
              </div>
            </div>
          </div>
        </div>

        <!-- Kunde + weitere Bestellungen -->
        <div class="sot__card">
          <div class="sot__cardHead"><v-icon small class="sot__cardIcon">mdi-account-outline</v-icon>Kunde</div>
          <div class="sot__custName">{{ order.customer && order.customer.firstName }}</div>
          <div v-if="!isEditEmail" class="sot__custEmail">
            <span>{{ order.customer && order.customer.email }}</span>
            <v-btn icon x-small title="Kopieren" @click="copyEmail"><v-icon small>mdi-content-copy</v-icon></v-btn>
            <v-btn icon x-small title="Ändern" @click="editEmail(true)"><v-icon small>mdi-pencil</v-icon></v-btn>
          </div>
          <div class="d-flex align-center" v-else>
            <v-text-field dense outlined hide-details label="Kunden-E-Mail" v-model="customEmail" />
            <v-btn :loading="isSavingEmail" @click="saveCustomEmail" icon><v-icon>mdi-content-save</v-icon></v-btn>
            <v-btn @click="editEmail(false)" icon><v-icon>mdi-close-circle</v-icon></v-btn>
          </div>
          <div class="sot__subhead">Weitere Bestellungen<v-progress-circular v-if="loadingCustomerOrders" indeterminate size="12" width="2" class="ml-1" /></div>
          <div v-if="!loadingCustomerOrders && !customerOrders.length" class="sot__muted">Keine weiteren Bestellungen mit dieser E-Mail.</div>
          <div v-else class="sot__orderLinks">
            <div v-for="o in customerOrders" :key="o.uid" class="sot__orderLink" @click="openOrderModal(String(o.uid))">
              <b>{{ o.orderName }}</b>
              <span class="ov-status ov-status--sm" :class="'ov-status--' + String(o.status || '').toLowerCase()">{{ o.status }}</span>
              <span class="sot__muted">{{ ago(o.orderDate) }}</span>
            </div>
          </div>
        </div>

        <!-- Versandadresse -->
        <div class="sot__card" v-if="order.shippingAddress">
          <div class="sot__cardHead"><v-icon small class="sot__cardIcon">mdi-map-marker-outline</v-icon>Versandadresse<v-spacer /><v-btn icon x-small @click="showEditAddress = true"><v-icon small>mdi-pencil</v-icon></v-btn></div>
          <div class="sot__address">
            <div v-if="order.shippingAddress.company">{{ order.shippingAddress.company }}</div>
            <div>{{ order.shippingAddress.firstName }} {{ order.shippingAddress.lastName }}</div>
            <div>{{ order.shippingAddress.addressPrimary }}</div>
            <div v-if="order.shippingAddress.addressSecondary">{{ order.shippingAddress.addressSecondary }}</div>
            <div>{{ order.shippingAddress.postCode }} {{ order.shippingAddress.city }}</div>
            <div>{{ order.shippingAddress.province }} {{ order.shippingAddress.countryCode }}</div>
            <div v-if="order.shippingAddress.phone" class="sot__muted"><v-icon x-small class="mr-1">mdi-phone-outline</v-icon>{{ order.shippingAddress.phone }}</div>
          </div>
          <EditOrderAddressDialog :show="showEditAddress" :order="order" :on-close="() => (showEditAddress = false)" />
        </div>
      </template>
    </template>

    <!-- Dialoge -->
    <OrderMailDialog v2 v-if="showMailDialog" :show="showMailDialog" :on-close="() => (showMailDialog = false)" :orderId="String(ticket.orderUid)" mail-category="SUPPORT" />
    <ReproduceOrderDialog v-if="showReproduce" :order-id="String(ticket.orderUid)" :show="showReproduce" :on-close="() => (showReproduce = false)" />
    <ManualFulfillDialog v-if="order && showManualFulfill" :order-id="String(order.uid)" :show="showManualFulfill" :onClose="() => (showManualFulfill = false)" />
    <ConfirmDialog :loading="isCanceling" icon="mdi-alert" message="Die gesamte Bestellung stornieren? Alle Tasks werden auf CANCELED gesetzt." :show="showCancelDialog" :on-accept="cancelOrder" :on-cancel="() => (showCancelDialog = false)" />
    <LiveEditorDialog
      v-if="liveEditorTask"
      v-model="showLiveEditor"
      :task="liveEditorTask"
      :customization-data="liveEditorTask.customizationData"
      :order-name="liveEditorTask.orderName"
      :upscaled-urls="{ DEFAULT: liveEditorTask.customizationData && liveEditorTask.customizationData.upscaledImage }"
      @approveProcessed="showLiveEditor = false"
      @approve="({ finalUrl, sendPreviewCustomer, refinementUrl, currentConfig }) => pushToFulFill(finalUrl, sendPreviewCustomer, refinementUrl, currentConfig)"
      @refinement="({ currentConfig, customFinalImage, revisionComment }) => pushToRefinement(revisionComment, customFinalImage, currentConfig)"
    />
    <v-dialog v-model="showChecklistDialog" max-width="440">
      <v-card class="pa-4">
        <div class="text-subtitle-1 font-weight-bold mb-1">Zur Checkliste: {{ order && order.orderName }}</div>
        <v-text-field v-model="checklistText" dense outlined hide-details autofocus placeholder="Was ist zu tun?" @keydown.enter="submitChecklistItem" />
        <v-select v-model="checklistAssignee" :items="checklistAssigneeItems" dense outlined hide-details class="mt-2" label="Zuweisen an" />
        <div class="d-flex justify-end mt-3" style="gap: 8px">
          <v-btn text rounded @click="showChecklistDialog = false">Abbrechen</v-btn>
          <v-btn rounded depressed color="#7b5cd6" dark :loading="checklistSaving" :disabled="!checklistText.trim()" @click="submitChecklistItem"><v-icon small left>mdi-plus</v-icon>Anlegen</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { shipmentFlagMeta, shipmentForItem, shipmentStatusMeta } from "@/utils/shipments";
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, setDoc, where } from "firebase/firestore";
import dayjs from "dayjs";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../../../fb";
import { addChecklistItem, copyToClipboard, countryFlagEmoji, getDateFromNow, getDateFromNowShort, getOrderStatusUrl, openOrderInShopify, openOrderModal } from "@/utils";
import { orderStatus, permissions, shopifyNameByShopId, taskStatus } from "@/constants";
import OrderMailDialog from "@/components/OrderMailDialog";
import ReproduceOrderDialog from "@/components/FulfillOrders/components/ReproduceOrderDialog";
import ManualFulfillDialog from "@/components/FulfillOrders/components/ManualFulfillDialog";
import EditOrderAddressDialog from "@/components/FulfillOrders/components/EditOrderAddressDialog";
import LiveEditorDialog from "@/components/FulfillOrders/LiveEditorDialog.vue";
import ConfirmDialog from "@/components/ConfirmDialog";
import { EventBus, events } from "../../../event-bus";

// Mittlere Spalte der Support-Ansicht: die Bestellung als komprimierte
// OrderView (Kopf + Aktionen, Fulfillment, Tasks mit Live Editor, Kunde,
// weitere Bestellungen, Versandadresse). Logik entspricht views/OrderView.vue.
export default {
  name: "SupportOrderTasks",
  components: { OrderMailDialog, ReproduceOrderDialog, ManualFulfillDialog, EditOrderAddressDialog, LiveEditorDialog, ConfirmDialog },
  props: {
    ticket: { type: Object, required: true },
  },
  data() {
    return {
      order: null,
      tasks: [],
      loading: true,
      unsubOrder: null,
      unsubTasks: [],
      customerOrders: [],
      loadingCustomerOrders: false,
      showMailDialog: false,
      showReproduce: false,
      showManualFulfill: false,
      showCancelDialog: false,
      showEditAddress: false,
      showChecklistDialog: false,
      showLiveEditor: false,
      liveEditorTask: null,
      isCanceling: false,
      holdSaving: false,
      isEditEmail: false,
      customEmail: "",
      isSavingEmail: false,
      checklistText: "",
      checklistAssignee: window.localStorage.getItem("td_checklist_default_assignee") || "",
      checklistSaving: false,
      releasingHold: false,
    };
  },
  computed: {
    ...mapGetters(["userPermissions", "allUsers"]),
    flag() {
      const cc =
        (this.order && this.order.shippingAddress && this.order.shippingAddress.countryCode) ||
        (this.order && this.order.countryCode) ||
        (this.ticket.order && (this.ticket.order.shippingCountryCode || this.ticket.order.countryCode));
      return cc ? countryFlagEmoji(cc) : "";
    },
    showFulfillOptions() {
      const p = this.userPermissions || [];
      return p.includes(permissions.ADMIN) || p.includes(permissions.FULFILL_ORDERS);
    },
    holdTooltip() {
      const h = this.order && this.order.fulfillHold;
      if (!h) return "";
      return `Fulfillment angehalten von ${(h.by && h.by.name) || "?"} ${h.at ? getDateFromNow(new Date(h.at)) : ""}`;
    },
    checklistAssigneeItems() {
      return [{ text: "👥 Alle", value: "" }, ...(this.allUsers || []).filter((u) => u.username).map((u) => ({ text: u.username, value: u.uid }))];
    },
    quelpHeldTickets() {
      return Object.values(this.ticket.quelpTickets || {}).filter((t) => t.onHold);
    },
    supportHoldReason() {
      if (this.quelpHeldTickets.length) return "Quelp-Ticket on hold: " + this.quelpHeldTickets.map((t) => t.subject || "Ohne Betreff").join(" · ") + ". Blockiert den Auto-Fulfill.";
      const legacy = Object.keys(this.ticket.data || {});
      if (legacy.length) return "Status-Seiten-Vorgang offen (" + legacy.join(", ") + "). Blockiert den Auto-Fulfill.";
      return "Support-Hold an der Bestellung. Blockiert den Auto-Fulfill.";
    },
    customerLabel() {
      const q = (this.ticket.quelpTickets || {})[this.ticket.quelpId] || Object.values(this.ticket.quelpTickets || {})[0];
      const c = (q && q.customer) || {};
      return [c.name, c.email].filter(Boolean).join(" · ") || this.ticket.email || "";
    },
  },
  watch: {
    "ticket.orderUid": { immediate: true, handler() { this.subscribe(); } },
  },
  beforeDestroy() {
    this.teardown();
  },
  methods: {
    shipmentForItem,
    shipmentStatusMeta,
    shipmentFlagMeta,

    ...mapActions(["updateLiveTask"]),
    ago(d) {
      return d ? getDateFromNowShort(d) : "";
    },
    dateOf(d) {
      return d ? dayjs(d).format("DD.MM. HH:mm") : "";
    },
    toDate(v) {
      if (!v) return null;
      return typeof v.toDate === "function" ? v.toDate() : v;
    },
    openOrderModal(uid) {
      openOrderModal(uid);
    },
    openOrder() {
      openOrderModal(String(this.ticket.orderUid));
    },
    teardown() {
      if (typeof this.unsubOrder === "function") this.unsubOrder();
      this.unsubOrder = null;
      this.unsubTasks.forEach((u) => typeof u === "function" && u());
      this.unsubTasks = [];
    },
    subscribe() {
      this.teardown();
      this.order = null;
      this.tasks = [];
      this.customerOrders = [];
      this.isEditEmail = false;
      if (this.ticket.noOrder) {
        this.loading = false;
        this.loadCustomerOrders(this.ticket.email);
        return;
      }
      this.loading = true;
      const orderUid = String(this.ticket.orderUid || "");
      if (!orderUid) {
        this.loading = false;
        return;
      }
      let first = true;
      this.unsubOrder = onSnapshot(doc(db, "orders", orderUid), async (snap) => {
        this.order = snap.exists() ? { uid: snap.id, ...snap.data() } : null;
        if (first && this.order) {
          first = false;
          this.loadCustomerOrders(this.order.customer && this.order.customer.email, orderUid);
        }
        const uids = ((this.order && this.order.tasks) || []).map((t) => t.taskUid).filter(Boolean);
        const known = this.tasks.map((t) => t.uid).join(",");
        if (known !== uids.join(",")) {
          this.unsubTasks.forEach((u) => typeof u === "function" && u());
          this.unsubTasks = [];
          const quantities = {};
          ((this.order && this.order.tasks) || []).forEach((t) => (quantities[t.taskUid] = t.quantity));
          const results = await Promise.all(
            uids.map(async (uid) => {
              const s = await getDoc(doc(collection(db, "tasks"), uid));
              return s.exists() ? { uid, quantity: quantities[uid], ...s.data() } : null;
            })
          );
          this.tasks = results.filter(Boolean);
          uids.forEach((uid, idx) => {
            this.unsubTasks.push(
              onSnapshot(doc(collection(db, "tasks"), uid), (ts) => {
                if (!ts.exists()) return;
                const next = [...this.tasks];
                next[idx] = { uid, quantity: quantities[uid], ...ts.data() };
                this.tasks = next.filter(Boolean);
              })
            );
          });
        }
        this.loading = false;
      });
    },
    async loadCustomerOrders(email, excludeUid = null) {
      const mail = String(email || "").trim().toLowerCase();
      this.customerOrders = [];
      if (!mail) return;
      this.loadingCustomerOrders = true;
      try {
        const snap = await getDocs(query(collection(db, "orders"), where("customer.email", "==", mail), limit(25)));
        this.customerOrders = snap.docs
          .map((d) => ({ uid: d.id, ...d.data() }))
          .filter((o) => !excludeUid || String(o.uid) !== String(excludeUid))
          .sort((a, b) => String(b.orderDate || "").localeCompare(String(a.orderDate || "")));
      } catch (e) {
        console.warn("Kunden-Bestellungen laden fehlgeschlagen:", e);
      } finally {
        this.loadingCustomerOrders = false;
      }
    },
    // ── Kopf-Aktionen (wie OrderView) ──
    openShopify() {
      openOrderInShopify({ orderId: this.order.uid, countryCode: this.order.countryCode, shopId: this.order.shopId });
    },
    openLoox() {
      const sub = shopifyNameByShopId[this.order.shopId] && shopifyNameByShopId[this.order.shopId][this.order.countryCode || "DE"];
      const email = encodeURIComponent((this.order.customer && this.order.customer.email) || "");
      window.open(`https://admin.shopify.com/store/${sub}/apps/loox-fashion-reviews/merchant/VkZs_igG0n/settings/orders?end_date=&q=${email}&range=&start_date=&status=`, "_blank");
    },
    copyStatusUrl() {
      const url = getOrderStatusUrl({ shopId: this.order.shopId, countryCode: this.order.countryCode, orderName: this.order.orderName, customerEmail: this.order.customer && this.order.customer.email, orderKey: this.order.orderKey });
      if (!url) {
        EventBus.$emit(events.SNACKBAR, { message: "Kein Status-Link für diesen Shop verfügbar", type: "error" });
        return;
      }
      copyToClipboard(url);
      EventBus.$emit(events.SNACKBAR, { message: "Status-Link kopiert", type: "success" });
    },
    async releaseAddressHold() {
      if (this.releasingHold) return;
      this.releasingHold = true;
      try {
        await setDoc(doc(db, "orders", this.order.uid), { resolveOnNewAddress: false, supportPending: !!this.quelpHeldTickets.length }, { merge: true });
        EventBus.$emit(events.SNACKBAR, { message: "Adress-Hold aufgehoben", type: "success" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: e.message, type: "error" });
      } finally {
        this.releasingHold = false;
      }
    },
    // Quelp-Hold lösen: läuft über Quelp (onHold=false → Hold-Trigger → /releaseHold).
    async releaseQuelpHold() {
      if (this.releasingHold) return;
      this.releasingHold = true;
      try {
        const fn = httpsCallable(functions, "quelpAction", { timeout: 70000 });
        for (const t of this.quelpHeldTickets) {
          const res = await fn({ action: "hold", ticketId: t.id, onHold: false });
          if (!res.data || res.data.status === "error") throw new Error((res.data && res.data.message) || "Hold lösen fehlgeschlagen");
        }
        EventBus.$emit(events.SNACKBAR, { message: "Quelp-Hold gelöst", type: "success" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: e.message, type: "error" });
      } finally {
        this.releasingHold = false;
      }
    },
    async toggleFulfillHold() {
      if (this.holdSaving) return;
      this.holdSaving = true;
      try {
        const next = this.order.fulfillHold ? null : { at: Date.now(), by: { uid: (auth.currentUser && auth.currentUser.uid) || null, name: (auth.currentUser && auth.currentUser.displayName) || "" } };
        await setDoc(doc(db, "orders", this.order.uid), { fulfillHold: next }, { merge: true });
      } catch (e) {
        console.error("Hold umschalten fehlgeschlagen", e);
      } finally {
        this.holdSaving = false;
      }
    },
    async cancelOrder() {
      this.isCanceling = true;
      try {
        const orderDoc = doc(collection(db, "orders"), this.order.uid);
        const res = await getDoc(orderDoc);
        for (const ot of (res.data() && res.data().tasks) || []) {
          await setDoc(doc(collection(db, "tasks"), ot.taskUid), { status: taskStatus.CANCELED }, { merge: true });
        }
        await setDoc(orderDoc, { status: orderStatus.CLOSED }, { merge: true });
        this.showCancelDialog = false;
      } finally {
        this.isCanceling = false;
      }
    },
    async submitChecklistItem() {
      const text = this.checklistText.trim();
      if (!text || this.checklistSaving) return;
      this.checklistSaving = true;
      try {
        const assignee = (this.allUsers || []).find((u) => u.uid === this.checklistAssignee);
        await addChecklistItem({ text, orderUid: this.order.uid, orderName: this.order.orderName, assignedTo: (assignee && assignee.uid) || null, assignedToName: (assignee && assignee.username) || null });
        window.localStorage.setItem("td_checklist_default_assignee", this.checklistAssignee || "");
        this.checklistText = "";
        this.showChecklistDialog = false;
      } catch (e) {
        console.error("Checklist: Anlegen fehlgeschlagen", e);
      } finally {
        this.checklistSaving = false;
      }
    },
    // ── Kunde ──
    copyEmail() {
      copyToClipboard(this.order.customer.email);
      EventBus.$emit(events.SNACKBAR, { message: "E-Mail kopiert", type: "success" });
    },
    editEmail(on) {
      this.customEmail = on ? this.order.customer.email : "";
      this.isEditEmail = on;
    },
    async saveCustomEmail() {
      this.isSavingEmail = true;
      try {
        await setDoc(doc(collection(db, "orders"), this.order.uid), { customer: { email: this.customEmail } }, { merge: true });
        EventBus.$emit(events.SNACKBAR, { message: `E-Mail geändert: ${this.customEmail}`, type: "success" });
        this.isEditEmail = false;
      } finally {
        this.isSavingEmail = false;
      }
    },
    // ── Tasks ──
    thumbUrl(t) {
      return t.finalTemplateThumbnailUrl || t.resultThumbnailUrl || t.thumb || "";
    },
    fullUrl(t) {
      return t.finalTemplateUrl || t.resultUrl || t.finalTemplateThumbnailUrl || t.resultThumbnailUrl || t.thumb || "";
    },
    thumbStyle(t) {
      const url = this.thumbUrl(t);
      return url ? { backgroundImage: `url("${url}")` } : {};
    },
    openTaskImage(t) {
      const url = this.fullUrl(t);
      if (url) window.open(url, "_blank");
    },
    runningEdit(t) {
      const cd = t.customizationData || {};
      return !!((cd.regionEditsRunning && cd.regionEditsRunning.count) || t.aiEditPending || (t.feedback && t.feedback.autoEdit && t.feedback.autoEdit.status === "running"));
    },
    openLiveEditor(t) {
      this.liveEditorTask = t;
      this.$nextTick(() => (this.showLiveEditor = true));
    },
    // Live Editor: Freigabe / Überarbeitung wie in TaskDetailsCard (Store-Action updateLiveTask).
    pushToFulFill(url = null, sendPreviewCustomer = false, refinementUrl, currentConfig) {
      const done = () => {
        this.showLiveEditor = false;
        EventBus.$emit(events.SNACKBAR, { message: "Task freigegeben", type: "success" });
      };
      this.updateLiveTask({ task: { ...this.liveEditorTask }, finalArtworkUrl: url, refinementUrl, currentConfig, callBack: done, sendPreviewCustomer, resolveFeedback: true });
    },
    pushToRefinement(revisionComment, refinementUrl, currentConfig) {
      const done = () => {
        this.showLiveEditor = false;
        EventBus.$emit(events.SNACKBAR, { message: "Task in Überarbeitung", type: "success" });
      };
      this.updateLiveTask({ task: { ...this.liveEditorTask }, refinementUrl, revisionComment, currentConfig, status: taskStatus.REFINEMENT, callBack: done, sendPreviewCustomer: false });
    },
  },
};
</script>

<style lang="scss" scoped>
.sot {
  display: flex; flex-direction: column; gap: 10px;
  &__card { background: #fff; border-radius: 14px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07); padding: 12px 14px; display: flex; flex-direction: column; gap: 6px; }
  &__cardHead { display: flex; align-items: center; gap: 6px; font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: rgba(0, 0, 0, 0.55); margin-bottom: 2px; }
  &__cardIcon { color: #7b5cd6 !important; }
  &__title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  &__flag { font-size: 18px; }
  &__orderName { font-weight: 800; font-size: 16px; color: #282828; cursor: pointer; &:hover { color: #7b5cd6; } }
  &__meta { font-size: 12px; color: rgba(0, 0, 0, 0.55); display: flex; gap: 6px; flex-wrap: wrap; }
  &__hint { font-size: 12px; color: rgba(0, 0, 0, 0.6); margin-top: 4px; }
  &__actions { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 4px; }
  &__btn {
    display: inline-flex; align-items: center; gap: 4px; border: 1px solid rgba(0, 0, 0, 0.12); background: #fff; color: #282828; font-size: 11px; font-weight: 600; border-radius: 999px; padding: 3px 9px; cursor: pointer; text-decoration: none; line-height: 1.4;
    &:hover { background: #f4f2fb; border-color: rgba(123, 92, 214, 0.4); }
    &:disabled { opacity: 0.5; cursor: default; }
    &--dark { background: #282828; color: #fff; border-color: #282828; &:hover { background: #3a3a3a; } }
    &--danger { color: #c62828; border-color: rgba(198, 40, 40, 0.35); &:hover { background: #ffebee; } }
    &--hold { background: #f0a92e; border-color: #f0a92e; color: #fff; .v-icon { color: #fff !important; } }
    &--accent { background: #edeafa; border-color: transparent; color: #6a4fc4; }
    .v-icon { font-size: 14px !important; }
  }
  &__muted { font-size: 12px; color: rgba(0, 0, 0, 0.5); display: inline-flex; align-items: center; }
  &__mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; color: rgba(0, 0, 0, 0.65); }
  &__link { color: #6a4fc4; font-weight: 600; text-decoration: underline; font-size: 12px; }
  &__chipBtn { margin-left: 4px; border: 0; background: rgba(178, 106, 0, 0.15); color: #b26a00; font-size: 10px; font-weight: 700; border-radius: 999px; padding: 0 6px; cursor: pointer; &:hover { background: rgba(178, 106, 0, 0.28); } &:disabled { opacity: 0.5; } }
  &__chip { display: inline-flex; align-items: center; gap: 3px; font-size: 10px; font-weight: 700; border-radius: 999px; padding: 1px 8px; background: #edeafa; color: #6a4fc4;
    &--express { background: #fff3e0; color: #b26a00; } &--warn { background: #ffebee; color: #c62828; } &--run { background: #e3f2fd; color: #1565c0; }
    &--hold { background: #fff3e0; color: #b26a00; } &--ok { background: #e8f5e9; color: #2e7d32; } &--wait { background: #fff3e0; color: #b26a00; } &--bad { background: #ffebee; color: #c62828; } }
  &__ff { display: flex; flex-direction: column; gap: 6px; padding-top: 4px; }
  &__ffMeta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  &__ffItem { border: 1px solid rgba(0, 0, 0, 0.06); border-radius: 10px; padding: 6px 10px; display: flex; flex-direction: column; gap: 3px; }
  &__ffItemTop { display: flex; align-items: center; gap: 8px; font-size: 12px; flex-wrap: wrap; }
  &__ffItemRow { display: flex; gap: 12px; flex-wrap: wrap; font-size: 11px; color: rgba(0, 0, 0, 0.65); align-items: center; }
  &__ok { color: #2e7d32; display: inline-flex; align-items: center; gap: 2px; }
  &__no { color: rgba(0, 0, 0, 0.5); display: inline-flex; align-items: center; gap: 2px; }
  &__task { display: grid; grid-template-columns: 84px minmax(0, 1fr); gap: 10px; padding: 8px 0; border-top: 1px solid rgba(0, 0, 0, 0.05); &--canceled { opacity: 0.55; } }
  &__thumb { width: 84px; height: 84px; border-radius: 10px; background: #f1f0f5 center/cover no-repeat; cursor: pointer; }
  &__taskBody { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  &__taskTop { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  &__status { font-weight: 700; font-size: 11px; border-radius: 999px; padding: 2px 10px; background: #f1f0f5; color: rgba(0, 0, 0, 0.6);
    &--draw { background: #fff3e0; color: #b26a00; } &--templating, &--review { background: #e3f2fd; color: #1565c0; } &--refinement { background: #ede7f6; color: #5e35b1; }
    &--fulfilled, &--closed { background: #e8f5e9; color: #2e7d32; } &--canceled { background: #ffebee; color: #c62828; } }
  &__product { font-weight: 700; font-size: 13px; color: #282828; margin-top: 2px; }
  &__qty { margin-left: 6px; font-size: 10px; background: #edeafa; color: #6a4fc4; border-radius: 999px; padding: 0 6px; }
  &__variant { font-size: 12px; color: rgba(0, 0, 0, 0.6); }
  &__extras { font-size: 11px; color: rgba(0, 0, 0, 0.5); display: flex; gap: 8px; flex-wrap: wrap; }
  &__taskMeta { font-size: 11px; color: rgba(0, 0, 0, 0.45); display: flex; gap: 10px; flex-wrap: wrap; margin-top: 2px; }
  &__taskBtns { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 4px; }
  &__custName { font-weight: 700; font-size: 14px; color: #282828; }
  &__custEmail { display: flex; align-items: center; gap: 4px; font-size: 12px; color: rgba(0, 0, 0, 0.7); }
  &__subhead { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: rgba(0, 0, 0, 0.45); font-weight: 700; margin-top: 6px; display: flex; align-items: center; }
  &__orderLinks { display: flex; flex-direction: column; gap: 4px; }
  &__orderLink { display: flex; align-items: center; gap: 8px; font-size: 12px; padding: 4px 8px; border-radius: 8px; cursor: pointer; &:hover { background: #f4f2fb; } }
  &__address { font-size: 12.5px; color: #282828; line-height: 1.45; }
}
.ov-status {
  font-weight: 700; font-size: 11px; border-radius: 999px; padding: 2px 10px; background: #f1f0f5; color: rgba(0, 0, 0, 0.6); white-space: nowrap;
  &--sm { font-size: 10px; padding: 1px 8px; }
  &--ready_for_fulfill { background: #e3f2fd; color: #1565c0; }
  &--waiting_for_fulfill, &--in_progress, &--order_created { background: #fff3e0; color: #b26a00; }
  &--in_fulfill { background: #ede7f6; color: #5e35b1; }
  &--fulfilled { background: #e8f5e9; color: #2e7d32; }
  &--fulfill_error { background: #ffebee; color: #c62828; }
  &--closed { background: #f1f0f5; color: rgba(0, 0, 0, 0.5); }
}
.sot__ship { font-weight: 600; font-size: 12px; display: inline-flex; align-items: center; gap: 4px; flex-wrap: wrap; }
</style>
